import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { WorkflowDto } from 'src/api/FiretrolApiClient';

export interface IWorkflowSelectorProps {
  resourceId: string;
  onChange: (value: WorkflowDto) => void;
  value: WorkflowDto | null;
  options: WorkflowDto[];
  disabled?: boolean;
  label?: string;
}

/** @registry Select dropdown of workflows scoped by division/resourceId; also exports WorkflowSelectorById for selecting by a known workflow id. */
export const WorkflowSelector: React.FC<IWorkflowSelectorProps> = ({
  onChange,
  value,
  resourceId,
  options,
  disabled,
  label,
}) => {
  const filteredOptions = React.useMemo(() => {
    return options.filter((o) => o.resourceId === resourceId);
  }, [options, resourceId]);

  const data = React.useMemo(() => {
    return filteredOptions.map((o) => ({
      value: String(o.id),
      label: `${o.name} ${o.office ? `(${o.office.name})` : '(Global)'}${
        o.isTemplate ? ' - Template' : ''
      }${o.jobId ? ' - Custom' : ''}`,
    }));
  }, [filteredOptions]);

  const selectValue = value ? String(value.id) : null;

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedWorkflow = filteredOptions.find(
        (o) => String(o.id) === selectedValue,
      );
      if (selectedWorkflow) {
        onChange(selectedWorkflow);
      }
    }
  };

  return (
    <div>
      <Select
        label={label}
        placeholder="Select the default workflow..."
        data={data}
        value={selectValue}
        onChange={handleChange}
        disabled={disabled}
        searchable
        clearable
      />
    </div>
  );
};

export interface IWorkflowSelectorByIdProps {
  resourceId: string;
  onChange: (value: number | null) => void;
  value: number | null;
  options: WorkflowDto[];
  disabled?: boolean;
  label?: string;
}

export const WorkflowSelectorById: React.FC<IWorkflowSelectorByIdProps> = ({
  onChange,
  value,
  resourceId,
  options,
  disabled,
  label,
}) => {
  const filteredOptions = React.useMemo(() => {
    return options.filter((o) => o.resourceId === resourceId);
  }, [options, resourceId]);

  const data = React.useMemo(() => {
    return filteredOptions.map((o) => ({
      value: String(o.id),
      label: `${o.name} ${o.office ? `(${o.office.name})` : '(Global)'}${
        o.isTemplate ? ' - Template' : ''
      }${o.jobId ? ' - Custom' : ''}`,
    }));
  }, [filteredOptions]);

  const selectValue = value ? String(value) : null;

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      onChange(Number(selectedValue));
    } else {
      onChange(null);
    }
  };

  return (
    <div>
      <Select
        label={label}
        placeholder="Select the default workflow..."
        data={data}
        value={selectValue}
        onChange={handleChange}
        disabled={disabled}
        searchable
        clearable
      />
    </div>
  );
};
